import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Mic,
  MessageCircle,
  Building2,
  MapPin,
  Flame,
  Droplets,
  Zap,
  Wrench,
  ArrowRight,
  Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// State data
const stateData = {
  "AL": { name: "Alabama", region: "Southeast" },
  "AK": { name: "Alaska", region: "Pacific" },
  "AZ": { name: "Arizona", region: "Southwest" },
  "AR": { name: "Arkansas", region: "South Central" },
  "CA": { name: "California", region: "Pacific" },
  "CO": { name: "Colorado", region: "Mountain" },
  "CT": { name: "Connecticut", region: "Northeast" },
  "DE": { name: "Delaware", region: "Mid-Atlantic" },
  "FL": { name: "Florida", region: "Southeast" },
  "GA": { name: "Georgia", region: "Southeast" },
  "HI": { name: "Hawaii", region: "Pacific" },
  "ID": { name: "Idaho", region: "Pacific Northwest" },
  "IL": { name: "Illinois", region: "Midwest" },
  "IN": { name: "Indiana", region: "Midwest" },
  "IA": { name: "Iowa", region: "Midwest" },
  "KS": { name: "Kansas", region: "Midwest" },
  "KY": { name: "Kentucky", region: "Southeast" },
  "LA": { name: "Louisiana", region: "South Central" },
  "ME": { name: "Maine", region: "Northeast" },
  "MD": { name: "Maryland", region: "Mid-Atlantic" },
  "MA": { name: "Massachusetts", region: "Northeast" },
  "MI": { name: "Michigan", region: "Midwest" },
  "MN": { name: "Minnesota", region: "Midwest" },
  "MS": { name: "Mississippi", region: "Southeast" },
  "MO": { name: "Missouri", region: "Midwest" },
  "MT": { name: "Montana", region: "Mountain" },
  "NE": { name: "Nebraska", region: "Midwest" },
  "NV": { name: "Nevada", region: "Southwest" },
  "NH": { name: "New Hampshire", region: "Northeast" },
  "NJ": { name: "New Jersey", region: "Mid-Atlantic" },
  "NM": { name: "New Mexico", region: "Southwest" },
  "NY": { name: "New York", region: "Northeast" },
  "NC": { name: "North Carolina", region: "Southeast" },
  "ND": { name: "North Dakota", region: "Midwest" },
  "OH": { name: "Ohio", region: "Midwest" },
  "OK": { name: "Oklahoma", region: "South Central" },
  "OR": { name: "Oregon", region: "Pacific Northwest" },
  "PA": { name: "Pennsylvania", region: "Mid-Atlantic" },
  "RI": { name: "Rhode Island", region: "Northeast" },
  "SC": { name: "South Carolina", region: "Southeast" },
  "SD": { name: "South Dakota", region: "Midwest" },
  "TN": { name: "Tennessee", region: "Southeast" },
  "TX": { name: "Texas", region: "South Central" },
  "UT": { name: "Utah", region: "Mountain" },
  "VT": { name: "Vermont", region: "Northeast" },
  "VA": { name: "Virginia", region: "Southeast" },
  "WA": { name: "Washington", region: "Pacific Northwest" },
  "WV": { name: "West Virginia", region: "Mid-Atlantic" },
  "WI": { name: "Wisconsin", region: "Midwest" },
  "WY": { name: "Wyoming", region: "Mountain" }
};

const industryData = {
  hvac: { name: "HVAC & Heating", icon: Flame, color: "text-orange-400", bg: "bg-orange-500/20" },
  plumbing: { name: "Plumbing", icon: Droplets, color: "text-blue-400", bg: "bg-blue-500/20" },
  electrical: { name: "Electrical", icon: Zap, color: "text-yellow-400", bg: "bg-yellow-500/20" },
  home_repair: { name: "Home Repair & Remodeling", icon: Wrench, color: "text-slate-400", bg: "bg-slate-500/20" },
  dental: { name: "Dental & Medical", icon: Building2, color: "text-teal-400", bg: "bg-teal-500/20" },
  roofing: { name: "Roofing", icon: Building2, color: "text-stone-400", bg: "bg-stone-500/20" },
  landscaping: { name: "Landscaping", icon: Building2, color: "text-green-400", bg: "bg-green-500/20" },
  auto_repair: { name: "Auto Repair", icon: Building2, color: "text-red-400", bg: "bg-red-500/20" },
  septic: { name: "Septic Services", icon: Droplets, color: "text-amber-400", bg: "bg-amber-500/20" },
  restaurant: { name: "Restaurant", icon: Building2, color: "text-amber-400", bg: "bg-amber-500/20" },
  fishing_charter: { name: "Fishing Charter", icon: Building2, color: "text-indigo-400", bg: "bg-indigo-500/20" },
  sightseeing: { name: "Sightseeing Tours", icon: MapPin, color: "text-sky-400", bg: "bg-sky-500/20" },
  gym: { name: "Gym & Fitness", icon: Building2, color: "text-purple-400", bg: "bg-purple-500/20" },
  real_estate: { name: "Real Estate", icon: Building2, color: "text-emerald-400", bg: "bg-emerald-500/20" }
};

export default function StateDemoPage() {
  const { stateCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const industry = searchParams.get('industry') || 'hvac';
  
  const state = stateData[stateCode?.toUpperCase()];
  const industryInfo = industryData[industry] || industryData.hvac;
  const IndustryIcon = industryInfo.icon;
  
  if (!state) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">State not found</h1>
          <Button onClick={() => navigate('/us-demo')}>Back to Map</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/us-demo')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1 bg-slate-800 rounded-lg">
                  <MapPin className="w-4 h-4 text-sky-400" />
                  <span className="font-semibold">{state.name}</span>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1 ${industryInfo.bg} rounded-lg`}>
                  <IndustryIcon className={`w-4 h-4 ${industryInfo.color}`} />
                  <span className={industryInfo.color}>{industryInfo.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">
            AI Demo for{' '}
            <span className={industryInfo.color}>{industryInfo.name}</span>
            {' '}in {state.name}
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Experience how our AI handles customer inquiries for {industryInfo.name.toLowerCase()} businesses in the {state.region} region.
          </p>
        </motion.div>

        {/* Demo Options */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Voice Demo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-green-500/50 transition-colors group cursor-pointer"
            onClick={() => navigate(`/voice-demo/${industry}?state=${stateCode}`)}
            data-testid="voice-demo-card"
          >
            <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Mic className="w-7 h-7 text-green-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">AI Voice Demo</h3>
            <p className="text-slate-400 text-sm mb-4">
              Simulate a phone call with our AI. Speak naturally and hear how we respond to customer inquiries.
            </p>
            <div className="flex items-center gap-2 text-green-400 font-medium">
              <Phone className="w-4 h-4" />
              <span>Start Voice Call</span>
              <ArrowRight className="w-4 h-4 ml-auto group-hover:translate-x-1 transition-transform" />
            </div>
          </motion.div>

          {/* Chat Demo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-sky-500/50 transition-colors group cursor-pointer"
            onClick={() => navigate(`/chat-demo/${industry}?state=${stateCode}`)}
            data-testid="chat-demo-card"
          >
            <div className="w-14 h-14 rounded-xl bg-sky-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <MessageCircle className="w-7 h-7 text-sky-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">AI Chat Demo</h3>
            <p className="text-slate-400 text-sm mb-4">
              Text-based conversation with our AI. See how we handle scheduling, pricing questions, and emergencies.
            </p>
            <div className="flex items-center gap-2 text-sky-400 font-medium">
              <MessageCircle className="w-4 h-4" />
              <span>Start Chat</span>
              <ArrowRight className="w-4 h-4 ml-auto group-hover:translate-x-1 transition-transform" />
            </div>
          </motion.div>

          {/* Find Contractors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-purple-500/50 transition-colors group cursor-pointer"
            onClick={() => navigate(`/contractors/${stateCode}?industry=${industry}`)}
            data-testid="contractors-card"
          >
            <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <Building2 className="w-7 h-7 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">Find Local Contractors</h3>
            <p className="text-slate-400 text-sm mb-4">
              Browse verified {industryInfo.name.toLowerCase()} providers in {state.name}. Read reviews and connect directly.
            </p>
            <div className="flex items-center gap-2 text-purple-400 font-medium">
              <Building2 className="w-4 h-4" />
              <span>Browse Providers</span>
              <ArrowRight className="w-4 h-4 ml-auto group-hover:translate-x-1 transition-transform" />
            </div>
          </motion.div>
        </div>

        {/* Industry Switcher */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-slate-500 text-sm mb-4">Try a different industry:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {Object.entries(industryData).map(([slug, info]) => {
              const Icon = info.icon;
              const isActive = slug === industry;
              return (
                <button
                  key={slug}
                  onClick={() => navigate(`/demo/${stateCode}?industry=${slug}`)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    isActive 
                      ? `${info.bg} border-current ${info.color}` 
                      : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{info.name}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Back to Map */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <Button
            variant="outline"
            onClick={() => navigate('/us-demo')}
            className="border-slate-700 text-slate-400 hover:text-white"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Back to US Map
          </Button>
        </motion.div>
      </main>
    </div>
  );
}
